/* eslint-disable no-undef */
import * as MP4Box from 'mp4box';

/**
 * Taken from https://github.com/w3c/webcodecs/blob/main/samples/mp4-decode/mp4_demuxer.js
 */
class Writer {
    constructor(size) {
        this.data = new Uint8Array(size);
        this.idx  = 0;
        this.size = size;
    }

    getData() {
        if (this.idx !== this.size) {
            throw new Error('Mismatch between size reserved and sized used');
        }
        return this.data.slice(0, this.idx);
    }

    writeUint8(value) {
        this.data.set([value], this.idx);
        this.idx += 1;
    }

    writeUint16(value) {
        const arr    = new Uint16Array(1);
        arr[0]       = value;
        const buffer = new Uint8Array(arr.buffer);
        this.data.set([buffer[1], buffer[0]], this.idx);
        this.idx += 2;
    }

    writeUint8Array(value) {
        this.data.set(value, this.idx);
        this.idx += value.length;
    }
}

/**
 * Taken from https://github.com/w3c/webcodecs/blob/main/samples/mp4-decode/mp4_demuxer.js
 *
 * Adapted to be more defensive for different MP4Box avcC layouts.
 *
 * @param avccBox
 * @param debug
 * @returns {*}
 */
const getExtradata = (avccBox, debug = false) => {
    const spsList = Array.isArray(avccBox.SPS) ? avccBox.SPS : [];
    const ppsList = Array.isArray(avccBox.PPS) ? avccBox.PPS : [];

    if (spsList.length === 0 || ppsList.length === 0) {
        if (debug) {
            // eslint-disable-next-line no-console
            console.error('avcC box has empty SPS/PPS arrays', {
                SPS : avccBox.SPS,
                PPS : avccBox.PPS
            });
        }
        throw new Error('avcC box has no SPS/PPS NAL units.');
    }

    const normalizeNalu = (entry, index, type) => {
        if (!entry) {
            if (debug) {
                // eslint-disable-next-line no-console
                console.error(`Invalid ${type} entry at index ${index}: entry is null/undefined`);
            }
            throw new Error(`Invalid ${type} entry at index ${index}`);
        }

        const rawNalu = entry.nalu || entry.data || entry;

        if (!rawNalu) {
            if (debug) {
                // eslint-disable-next-line no-console
                console.error(`Missing NALU data for ${type} at index ${index}`, entry);
            }
            throw new Error(`Missing NALU data for ${type} at index ${index}`);
        }

        // Normalize to Uint8Array for Writer.writeUint8Array
        if (rawNalu instanceof Uint8Array) {
            return rawNalu;
        }

        if (rawNalu.buffer instanceof ArrayBuffer) {
            return new Uint8Array(rawNalu.buffer);
        }

        if (rawNalu instanceof ArrayBuffer) {
            return new Uint8Array(rawNalu);
        }

        if (debug) {
            // eslint-disable-next-line no-console
            console.error(`Unsupported NALU type for ${type} at index ${index}`, rawNalu);
        }
        throw new Error(`Unsupported NALU type for ${type} at index ${index}`);
    };

    let i;
    let size = 7;

    for (i = 0; i < spsList.length; i += 1) {
        const nalu = normalizeNalu(spsList[i], i, 'SPS');
        // nalu length is encoded as a uint16.
        size += 2 + nalu.length;
    }

    for (i = 0; i < ppsList.length; i += 1) {
        const nalu = normalizeNalu(ppsList[i], i, 'PPS');
        // nalu length is encoded as a uint16.
        size += 2 + nalu.length;
    }

    const writer = new Writer(size);

    writer.writeUint8(avccBox.configurationVersion);
    writer.writeUint8(avccBox.AVCProfileIndication);
    writer.writeUint8(avccBox.profile_compatibility);
    writer.writeUint8(avccBox.AVCLevelIndication);
    // eslint-disable-next-line no-bitwise
    writer.writeUint8(avccBox.lengthSizeMinusOne + (63 << 2));

    // eslint-disable-next-line no-bitwise
    writer.writeUint8(avccBox.nb_SPS_nalus + (7 << 5));
    for (i = 0; i < spsList.length; i += 1) {
        const nalu = normalizeNalu(spsList[i], i, 'SPS');
        writer.writeUint16(nalu.length);
        writer.writeUint8Array(nalu);
    }

    writer.writeUint8(avccBox.nb_PPS_nalus);
    for (i = 0; i < ppsList.length; i += 1) {
        const nalu = normalizeNalu(ppsList[i], i, 'PPS');
        writer.writeUint16(nalu.length);
        writer.writeUint8Array(nalu);
    }

    return writer.getData();
};

/**
 * decodeVideo takes an url to a mp4 file and converts it into frames.
 *
 * The steps for this are:
 *  1. Determine the codec for this video file and demux it into chunks.
 *  2. Read the chunks with VideoDecoder as fast as possible.
 *  3. Return an array of frames that we can efficiently draw to a canvas.
 *
 * @param src
 * @param VideoDecoder
 * @param EncodedVideoChunk
 * @param emitFrame
 * @param debug
 * @returns {Promise<unknown>}
 */
const decodeVideo = (
    src,
    emitFrame,
    {
        VideoDecoder,
        EncodedVideoChunk,
        debug
    }
) => new Promise((resolve, reject) => {
    if (debug) console.info('Decoding video from', src);

    try {
        // Uses mp4box for demuxing
        const mp4boxfile = MP4Box.createFile();

        // Holds the codec value
        let codec;

        // Creates a VideoDecoder instance
        const decoder = new VideoDecoder({
            output : (frame) => {
                createImageBitmap(frame, { resizeQuality : 'low' }).then((bitmap) => {
                    emitFrame(bitmap);
                    frame.close();

                    if (decoder.decodeQueueSize <= 0) {
                        // Give it an extra half second to finish everything
                        setTimeout(() => {
                            if (decoder.state !== 'closed') {
                                decoder.close();
                                resolve();
                            }
                        }, 500);
                    }
                });
            },
            error : (e) => {
                // eslint-disable-next-line no-console
                console.error(e);
                reject(e);
            }
        });

        // Propagate mp4box-level errors to the promise
        mp4boxfile.onError = (error) => {
            if (debug) {
                // eslint-disable-next-line no-console
                console.error('MP4Box error while decoding video', error);
            }
            reject(error);
        };

        mp4boxfile.onReady = (info) => {
            try {
                if (info && info.videoTracks && info.videoTracks[0]) {
                    [{ codec }] = info.videoTracks;
                    if (debug) console.info('Video with codec:', codec);

                    // Defensive checks for avcC box presence
                    const moov  = mp4boxfile.moov;
                    const traks = moov && moov.traks;
                    const trak  = traks && traks[0];
                    const mdia  = trak && trak.mdia;
                    const minf  = mdia && mdia.minf;
                    const stbl  = minf && minf.stbl;
                    const stsd  = stbl && stbl.stsd;
                    const entry = stsd && stsd.entries && stsd.entries[0];
                    const avccBox = entry && entry.avcC;

                    if (!avccBox) {
                        throw new Error('Unable to locate avcC box for video track.');
                    }

                    const extradata = getExtradata(avccBox, debug);

                    // configure decoder
                    decoder.configure({
                        codec,
                        description : extradata
                    });

                    // Setup mp4box file for breaking it into chunks
                    mp4boxfile.setExtractionOptions(info.videoTracks[0].id);
                    mp4boxfile.start();
                } else {
                    reject(new Error('URL provided is not a valid mp4 video file.'));
                }
            } catch (error) {
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.error('Error while preparing WebCodecs decoder', error);
                }
                reject(error);
            }
        };

        mp4boxfile.onSamples = (track_id, ref, samples) => {
            for (let i = 0; i < samples.length; i += 1) {
                const sample = samples[i];
                const type   = sample.is_sync ? 'key' : 'delta';

                const chunk = new EncodedVideoChunk({
                    type,
                    timestamp : sample.cts,
                    duration  : sample.duration,
                    data      : sample.data
                });

                decoder.decode(chunk);
            }
        };

        // Fetches the file into arraybuffers
        const loadSource = () => {
            // For blob: URLs it is simpler and more reliable to read the whole
            // ArrayBuffer at once and feed it to mp4box.
            if (typeof src === 'string' && src.startsWith('blob:')) {
                return fetch(src)
                    .then((res) => res.arrayBuffer())
                    .then((buffer) => {
                        const buf = buffer;
                        // mp4box expects a fileStart field for each appended buffer
                        buf.fileStart = 0;
                        mp4boxfile.appendBuffer(buf);
                        mp4boxfile.flush();
                    });
            }

            // Default path: stream response chunks into mp4box
            return fetch(src).then((res) => {
                const { body } = res;

                if (!body || typeof body.getReader !== 'function') {
                    throw new Error('ReadableStream is not available for video response body.');
                }

                const reader = body.getReader();
                let offset   = 0;

                function appendBuffers({
                    done,
                    value
                }) {
                    if (done) {
                        mp4boxfile.flush();
                        return null;
                    }

                    const buf     = value.buffer;
                    buf.fileStart = offset;
                    offset += buf.byteLength;
                    mp4boxfile.appendBuffer(buf);

                    return reader.read().then(appendBuffers);
                }

                return reader.read().then(appendBuffers);
            });
        };

        loadSource().catch((e) => {
            if (debug) {
                // eslint-disable-next-line no-console
                console.error('Error while fetching video for WebCodecs decoding', e);
            }
            reject(e);
        });
    } catch (e) {
        reject(e);
    }
});

/**
 * The main function for decoding video. Deals with the polyfill cases first,
 * then calls our decodeVideo.
 *
 * @param src
 * @param emitFrame
 * @param debug
 * @returns {Promise<never>|Promise<void>|*}
 */
export default (src, emitFrame, debug) => {
    // If our browser supports WebCodecs natively
    if (
        typeof VideoDecoder === 'function'
        && typeof EncodedVideoChunk === 'function'
    ) {
        if (debug) {
            console.info('WebCodecs is natively supported, using native version...');
        }
        return decodeVideo(src, emitFrame, {
            VideoDecoder,
            EncodedVideoChunk,
            debug
        });
    }

    // Otherwise, resolve nothing
    if (debug) console.info('WebCodecs is not available in this browser.');
    return Promise.resolve();
};
